import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { 
  MapPin, Clock, Navigation, ArrowRight, Calendar, 
  CheckCircle, Coffee
} from "lucide-react";

export default function NextVisitCard({ onNavigate }) {
  const [nextVisit, setNextVisit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNextVisit();
  }, []);

  const loadNextVisit = async () => {
    try {
      const { data } = await api.get("/evv/schedule/next-visit");
      setNextVisit(data);
    } catch (err) {
      console.error("Failed to load next visit:", err);
    } finally {
      setLoading(false);
    }
  };

  const openMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, "_blank");
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    );
  }

  // No more visits
  if (!nextVisit?.has_next_visit) {
    return (
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 border border-emerald-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-emerald-200 flex items-center justify-center">
            <Coffee className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-900">All Done for Today!</h3>
            <p className="text-sm text-emerald-700">No more visits scheduled</p>
          </div>
        </div>
        <Link
          to="/employee/schedule"
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-800"
          data-testid="view-schedule-link"
        >
          <Calendar className="w-4 h-4" />
          View Tomorrow&apos;s Schedule
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const visit = nextVisit.visit;
  const client = nextVisit.client;

  return (
    <div className="bg-gradient-to-br from-[#06372B] to-[#0a4a3a] rounded-2xl p-5 text-white" data-testid="next-visit-card">
      <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
        <ArrowRight className="w-4 h-4" />
        <span>Your Next Visit{nextVisit.is_today ? " - Today" : ""}</span>
      </div>
      
      <div className="font-semibold text-lg">{nextVisit.client_name}</div>
      
      {client?.address && (
        <div className="flex items-start gap-2 mt-2 text-white/80 text-sm">
          <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="line-clamp-2">{client.address}</span>
        </div>
      )}
      
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {visit?.scheduled_start_time && (
          <div className="flex items-center gap-1.5 text-sm bg-white/20 rounded-full px-3 py-1.5">
            <Clock className="w-3.5 h-3.5" />
            <span>{visit.scheduled_start_time}</span>
          </div>
        )}
        
        {client?.address && (
          <button
            onClick={() => openMaps(client.address)}
            className="flex items-center gap-1.5 text-sm bg-white/20 rounded-full px-3 py-1.5 hover:bg-white/30 transition-colors"
            data-testid="navigate-next-visit"
          >
            <Navigation className="w-3.5 h-3.5" />
            Navigate
          </button>
        )}
        
        {client?.phone && (
          <a
            href={`tel:${client.phone}`}
            className="flex items-center gap-1.5 text-sm bg-white/20 rounded-full px-3 py-1.5 hover:bg-white/30 transition-colors"
            data-testid="call-client"
          >
            Call Client
          </a>
        )}
      </div>

      {/* View Full Schedule Link */}
      <Link
        to="/employee/schedule"
        className="mt-4 flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
        data-testid="view-full-schedule"
      >
        <Calendar className="w-4 h-4" />
        View Full Schedule
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
